import { Component, OnInit, Input, inject, signal } from '@angular/core';
import { settledCache$ } from '../../shared/utils/cache-ready';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { filter, take } from 'rxjs';

import { Coi } from '../../objects/coi';
import { CoiService } from '../../service/entity-services/coi.service';

@Component({
  selector: 'app-coi-connections',
  templateUrl: './coi-connections.component.html',
  styleUrls: ['./coi-connections.component.css'],
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatChipsModule, MatProgressSpinnerModule]
})
export class CoiConnectionsComponent implements OnInit {
  private coiService = inject(CoiService);
  private router = inject(Router);

  coiObjectArray = signal<Coi[]>([]);
  waiting = signal<boolean>(true);

  @Input() selectedCoiList: any[] = [];

  ngOnInit(): void {
    this.hydrateCoiData();
  }

  private hydrateCoiData(): void {
    if (!this.selectedCoiList || this.selectedCoiList.length === 0) {
      console.log('[CoiConnections:Diag] No COI list provided, showing empty state');
      this.waiting.set(false);
      return;
    }

    this.waiting.set(true);

    // Extract COI identifiers, dedup
    const coiIds = Array.from(new Set(
      this.selectedCoiList.map(c =>
        typeof c === 'object' && c?.coiNum ? c.coiNum :
        typeof c === 'object' && c?.coiId ? c.coiId : c
      ).filter(id => !!id)
    ));

    console.log('[CoiConnections:Diag] Looking up COI IDs:', coiIds);

    if (coiIds.length === 0) {
      this.waiting.set(false);
      return;
    }

    // Wait for the COI cache to FINISH streaming, then look up directly.
    // `filter(arr => arr.length > 0)` resolved on page 1, so any linked COI on a
    // later page was dropped by the .find() below and silently vanished from the
    // tab — the list looked complete while being wrong.
    settledCache$(this.coiService.allCois$).subscribe({
      next: (allCois) => {
        console.log('[CoiConnections:Diag] COI cache size:', allCois.length);
        const matched = coiIds
          .map(id => allCois.find(coi => coi.coiId === id || coi.coiNum === id))
          .filter((coi): coi is Coi => coi !== null && coi !== undefined);
        console.log('[CoiConnections:Diag] Matched COIs:', matched.length, 'of', coiIds.length, 'requested');
        this.coiObjectArray.set(matched);
        this.waiting.set(false);
      },
      error: (err) => {
        console.error('Error loading COI connections:', err);
        this.waiting.set(false);
      }
    });
  }

  navigateToCoi(coi: any): void {
    const num = coi.coiNum || coi.coiId;
    if (num) {
      window.open(`/securehome/certificate-of-insurance/${num}`, '_blank');
    }
  }

  formatDate(date: string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  }

  extractPartyName(field: any): string {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field.name || field.friendlyName || field.vendorData?.name || '';
  }
}
